import { Component } from "react";
import IGetCategoriesDTO from "../../../../../models/DTO/IGetCategoriesDTO";
import IGetSubCategoriesDTO from "../../../../../models/DTO/IGetSubCategoriesDTO";
import IOnGetCategoriesDTO from "../../../../../models/DTO/IOnGetCategoriesDTO";
import IOnGetSubCategoriesDTO from "../../../../../models/DTO/IOnGetSubCategoriesDTO";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import ICatalog_Item from "../../../../../models/ICatalog_Item";
import ICategory from "../../../../../models/ICategory";
import ISupplierModel from "../../../../../models/ISupplierModel";
import IUserUI from "../../../../../models/IUserUI";
import OwSelectOption from "../../../../shared/OwSelectOption/OwSelectOption";
import { Socket } from "socket.io-client";
import { CategoryItemCategoryAttributes } from "../CategoryItemCategoryAttributes/CategoryItemCategoryAttributes";
import ICatalog_Item_Attribute from "../../../../../models/ICatalog_Item_Attribute";

type Props = {
    socket: Socket;
    catalogGuid?: string;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    setCategoryId: (id: number) => void;
    catalogItem?: ICatalog_Item;
    requiredCategories?: any;
    catalogItemAttributes?: ICatalog_Item_Attribute[];
    onAttributesChange?: (attributes: ICatalog_Item_Attribute[]) => void;
};

type State = {
    selectedCategory?: ICategory;
    selectedSubCategory?: ICategory;
    selectedSubSubCategory?: ICategory;
    selectedFinalCategory?: ICategory;
    categories: ICategory[];
    subCategories: ICategory[];
    subSubCategories: ICategory[];
    finalCategories: ICategory[];
    showRoot: boolean;
    showSubCat: boolean;
    showSubSubCat: boolean;
    showFinalCat: boolean;
};

class CategorySelection extends Component<Props, State> {
    readonly state: State = {
        categories: [],
        subCategories: [],
        subSubCategories: [],
        finalCategories: [],
        showRoot: true,
        showSubCat: true,
        showSubSubCat: true,
        showFinalCat: true,
    };

    componentDidMount(): void {
        this.props.socket.on(EndpointsEnum.CategoriesOnGetCategories, this.onGetCategories);
        this.props.socket.on(EndpointsEnum.CategoriesOnGetSubCategories, this.onGetSubCategories);

        let showRoot = true;
        let showSubCat = true;
        let showSubSubCat = true;
        let showFinalCat = true;

        if (this.props.requiredCategories) {
            const requiredCategories = this.props.requiredCategories;

            showRoot = requiredCategories.length >= 1;
            showSubCat = requiredCategories.length >= 2;
            showSubSubCat = requiredCategories.length >= 3;
            showFinalCat = requiredCategories.length >= 4;
        }

        this.setState(
            {
                showRoot,
                showSubCat,
                showSubSubCat,
                showFinalCat,
            },
            () => {
                this.getCategories();
            }
        );
    }

    componentWillUnmount(): void {
        this.props.socket.removeListener(EndpointsEnum.CategoriesOnGetCategories, this.onGetCategories);
        this.props.socket.removeListener(EndpointsEnum.CategoriesOnGetSubCategories, this.onGetSubCategories);
    }

    getCategories = () => {
        if (!this.props.catalogGuid) {
            return null;
        }

        const payload: IGetCategoriesDTO = {
            userGuid: this.props.userModel.guid,
            userToken: this.props.userModel.token,
            supplierGuid: this.props.supplierModel.guid,
            onlyRootCategories: true,
            catalogGuid: this.props.catalogGuid,
        };

        this.props.socket.emit(EndpointsEnum.CategoriesGetCategories, payload);
    };

    onGetCategories = (data: IOnGetCategoriesDTO) => {
        this.setState({
            categories: data.categories,
        });
    };

    getSubCategories = (parentCategoryGuid: string) => {
        if (!this.state.selectedCategory) {
            return null;
        }

        const payload: IGetSubCategoriesDTO = {
            userGuid: this.props.userModel.guid,
            userToken: this.props.userModel.token,
            supplierGuid: this.props.supplierModel.guid,
            parentCategoryGuid,
        };

        this.props.socket.emit(EndpointsEnum.CategoriesGetSubCategories, payload);
    };

    onGetSubCategories = (data: IOnGetSubCategoriesDTO) => {
        if (this.state.selectedCategory && data.parentCategoryGuid === this.state.selectedCategory.guid) {
            this.setState({
                subCategories: data.categories,
            });
        } else {
            if (this.state.selectedSubCategory && this.state.selectedSubCategory.guid === data.parentCategoryGuid) {
                this.setState({
                    subSubCategories: data.categories,
                });
            }

            if (this.state.selectedSubSubCategory && this.state.selectedSubSubCategory.guid === data.parentCategoryGuid) {
                this.setState({
                    finalCategories: data.categories,
                });
            }
        }
    };

    handleCategorySelection = (category: ICategory) => {
        // const value = e.target.value;
        // const name = e.target.name;
        // console.log("handleCategorySelection", category);

        const stateUpdate: any = {
            selectedCategory: category,
        };

        this.setState(stateUpdate, () => {
            // console.log("handleCategorySelection", this.state.selectedCategory, category);
            if (category) {
                this.props.setCategoryId(category.id);

                if (this.state.showSubCat && category.has_dependents && this.state.selectedCategory) {
                    this.getSubCategories(this.state.selectedCategory.guid);
                }
            } else {
                // reselect the as the previous category
                // @ts-expect-error
                this.props.setCategoryId(null);
            }
        });
    };

    handleSubCategorySelection = (selectedSubCategory: ICategory) => {
        const stateUpdate: any = {
            selectedSubCategory,
        };
        // console.log("handleSubCategorySelection", selectedSubCategory);

        this.setState(stateUpdate, () => {
            if (selectedSubCategory) {
                if (this.state.showSubSubCat && selectedSubCategory.has_dependents) {
                    this.getSubCategories(selectedSubCategory.guid);
                }

                this.props.setCategoryId(selectedSubCategory.id);
            } else {
                // reselect the as the previous category
                if (this.state.selectedCategory) {
                    this.props.setCategoryId(this.state.selectedCategory.id);
                }
            }
        });
    };

    handleSubSubCategorySelection = (selectedSubSubCategory: ICategory) => {
        const stateUpdate: any = {
            selectedSubSubCategory,
        };
        // console.log("handleSubSubCategorySelection", selectedSubSubCategory);

        this.setState(stateUpdate, () => {
            if (selectedSubSubCategory) {
                if (this.state.showFinalCat && selectedSubSubCategory.has_dependents) {
                    this.getSubCategories(selectedSubSubCategory.guid);
                }

                this.props.setCategoryId(selectedSubSubCategory.id);
            } else {
                // reselect the as the previous category
                if (this.state.selectedSubCategory) {
                    this.props.setCategoryId(this.state.selectedSubCategory.id);
                }
            }
        });
    };

    handleFinalCategorySelection = (finalCategory: any) => {
        // console.log("handleFinalCategorySelection", finalCategory);
        if (finalCategory) {
            this.props.setCategoryId(finalCategory.id);
        } else {
            // reselect the as the previous category
            if (this.state.selectedSubSubCategory) {
                this.props.setCategoryId(this.state.selectedSubSubCategory.id);
            }
        }
    };

    getRootCategory = (rootCategory?: ICategory) => {
        if (!this.state.categories) {
            return null;
        }

        // console.log("getRootCategory", rootCategory);

        return (
            <OwSelectOption
                titleProp={"name"}
                valueProp={"id"}
                name={"category"}
                options={this.state.categories}
                fn={this.handleCategorySelection}
                showBlankFirst={true}
                emitObject={true}
                emitOnLoad={true}
                selectedOption={rootCategory}
                selectedOptionProp={"id"}
                autoSelectSingleOption={false}
            />
        );
    };

    getSubCategory = (subCategory?: ICategory) => {
        if (!this.state.subCategories || !this.state.subCategories.length) {
            return null;
        }

        return (
            <div className={"mt-2"}>
                <OwSelectOption
                    titleProp={"name"}
                    valueProp={"id"}
                    name={"subCategory"}
                    options={this.state.subCategories}
                    fn={this.handleSubCategorySelection}
                    showBlankFirst={true}
                    emitObject={true}
                    emitOnLoad={true}
                    selectedOption={subCategory}
                    selectedOptionProp={"id"}
                    autoSelectSingleOption={false}
                />
            </div>
        );
    };

    getSubSubCategory = (subSubCategory?: ICategory) => {
        // console.log("getSubSubCategory");
        if (!this.state.subSubCategories || !this.state.subSubCategories.length) {
            return null;
        }

        return (
            <div className={"mt-2"}>
                <OwSelectOption
                    titleProp={"name"}
                    valueProp={"id"}
                    name={"subSubCategory"}
                    options={this.state.subSubCategories}
                    fn={this.handleSubSubCategorySelection}
                    showBlankFirst={true}
                    emitObject={true}
                    emitOnLoad={true}
                    nullIfBlank={true}
                    selectedOption={subSubCategory}
                    selectedOptionProp={"id"}
                    autoSelectSingleOption={false}
                />
            </div>
        );
    };

    getFinalCategory = (finalCategory?: ICategory) => {
        if (!this.state.finalCategories || !this.state.finalCategories.length) {
            return null;
        }

        return (
            <div className={"mt-2"}>
                <OwSelectOption
                    titleProp={"name"}
                    valueProp={"id"}
                    name={"finalCategory"}
                    options={this.state.finalCategories}
                    fn={this.handleFinalCategorySelection}
                    showBlankFirst={true}
                    emitObject={true}
                    emitOnLoad={true}
                    selectedOption={finalCategory}
                    selectedOptionProp={"id"}
                    autoSelectSingleOption={false}
                />
            </div>
        );
    };

    render() {
        const categoryHierarchy = this.props.catalogItem ? this.props.catalogItem.CategoryHierarchy : [];

        // If the Category is Set, We set the Child Category Id. But, We need to reconstruct it
        // back up from the youngest child up to the oldest, root ancestor.
        let rootCategory;
        let subCategory;
        let subSubCategory;
        let finalCategory;

        if (categoryHierarchy && categoryHierarchy.length) {
            for (let i = 0; i < categoryHierarchy.length; ++i) {
                switch (i) {
                    case 0:
                        rootCategory = categoryHierarchy[0] ? categoryHierarchy[0] : undefined;
                        break;

                    case 1:
                        subCategory = categoryHierarchy[1] ? categoryHierarchy[1] : undefined;
                        break;

                    case 2:
                        subSubCategory = categoryHierarchy[2] ? categoryHierarchy[2] : undefined;
                        break;

                    case 3:
                        finalCategory = categoryHierarchy[3] ? categoryHierarchy[3] : undefined;
                        break;
                }
            }
        }

        return (
            <div className="CategorySelection">
                {/*<pre>{this.state.selectedCategory ? `${this.state.selectedCategory.name} - ${this.state.selectedCategory.has_dependents}` : ""}</pre>*/}
                {/* Root Level */}
                {this.state.showRoot ? this.getRootCategory(rootCategory) : null}

                {/* 1st Level */}
                {this.state.showSubCat ? this.getSubCategory(subCategory) : null}

                {/* 2nd Level */}
                {this.state.showSubSubCat ? this.getSubSubCategory(subSubCategory) : null}

                {/* 3rd Level */}
                {this.state.showFinalCat ? this.getFinalCategory(finalCategory) : null}

                {this.props.onAttributesChange && (
                    <CategoryItemCategoryAttributes
                        socket={this.props.socket}
                        userModel={this.props.userModel}
                        supplierModel={this.props.supplierModel}
                        selectedCategory={this.state.selectedCategory}
                        selectedSubCategory={this.state.selectedSubCategory}
                        selectedSubSubCategory={this.state.selectedSubSubCategory}
                        selectedFinalCategory={this.state.selectedFinalCategory}
                        catalogItemAttributes={this.props.catalogItemAttributes}
                        onAttributesChange={this.props.onAttributesChange}
                    />
                )}
            </div>
        );
    }
}

export default CategorySelection;
